import React, { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Table,
} from "reactstrap";
import { post, put } from "helpers/api_helper";
import * as api from "../../constants/api_constants";

import "./UserManagement.scss";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import CustomPagination from "components/CustomPagination";
import SummaryCards from "components/SummaryCards";
import { showErrorToast, showSuccessToast } from "helpers/toastMessages";
import { CustomButton } from "components/CommonInputs/CustomButtons/CustomButton";

function Users() {
  const navigate = useNavigate();
  const [usersData, setUsersData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const timerRef = useRef(null);
  // Fetch users data with error handling and loading state
  const fetchData = useCallback(async (searchKeyword, page = 1) => {
    try {
      setIsLoading(true);
      const response = await post(api.GET_ALL_USERS, {
        searchKeyword: searchKeyword,
        pageSize: 10,
        page: page - 1,
        direction: "ASC",
        sortBy: "userId",
      });
      setUsersData(response?.data || []);
      setTotalPages(response?.totalPages);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Memoized data fetching on component mount
  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchData(searchQuery, page);
  };

  const handleSearchQuery = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      fetchData(query);
    }, 300);
  };

  // Memoized user status toggle handler
  const handleSwitchToggle = useCallback(
    async (userId) => {
      const userToUpdate = usersData.find((user) => user.userId === userId);
      if (!userToUpdate) return;

      try {
        setIsLoading(true);
        await put(api.UPDATE_USERDATA(userId), {
          ...userToUpdate,
          status: userToUpdate.status === 1 ? 0 : 1,
        });
        showSuccessToast("User status updated successfully");
        await fetchData(searchQuery, currentPage);
      } catch (error) {
        showErrorToast("Failed to update user status");
        console.error("Error updating user status:", error);
        // Optional: Add error notification
      }
    },
    [usersData, fetchData]
  );

  // Memoized filtered and searched users

  // Memoized user statistics
  const userStats = useMemo(
    () => ({
      total: usersData.length,
      active: usersData.filter((user) => user.status === 1).length,
      inactive: usersData.filter((user) => user.status === 0).length,
    }),
    [usersData]
  );

  const summaryData = [
    {
      iconClass: "fas fa-users text-primary",
      label: "Total Count",
      value: userStats.total,
    },
    {
      iconClass: "fas fa-user-check text-primary",
      label: "Active Count",
      value: userStats.active,
    },
    {
      iconClass: "fas fa-user-times text-danger",
      label: "Inactive Count",
      value: userStats.inactive,
    },
  ];

  return (
    <div className="page-content">
      <Container fluid={true} style={{ padding: "0px", gap: "10px" }}>
        <PageSubHeader heading="Users" routePath="Home" pageName="Users" />

        <Row className="px-4">
          {/* User Statistics */}
          <SummaryCards data={summaryData} />

          {/* Search and Add User */}
          <Row className="d-flex justify-content-between align-items-center py-2">
            <Col xs={3}>
              <InputGroup size="sm" className="elbert-search-group">
                <Input
                  placeholder="Search ID, Name, Email"
                  className="elbert-search"
                  value={searchQuery}
                  onChange={handleSearchQuery}
                />
                <InputGroupText>
                  <span className="fa fa-search"></span>
                </InputGroupText>
              </InputGroup>
            </Col>
            <Col
              xs={3}
              className="d-flex align-items-center justify-content-end"
            >
              {/* <Button
                color="primary"
                outline
                className="waves-effect waves-light"
                onClick={() => navigate("/add-user")}
              >
                Add User
              </Button> */}
              <CustomButton
                text={"+ Add New"}
                onClick={() => navigate("/add-user")}
              />
            </Col>
          </Row>

          {/* Users Table */}
          <Row className="py-3">
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="table-responsive">
                    <Table className="table mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: "15%" }}>Username</th>
                          <th style={{ width: "20%" }}>Email</th>
                          <th style={{ width: "15%" }}>Phone</th>
                          <th style={{ width: "10%" }}>Role</th>
                          <th style={{ width: "25%" }}>Description</th>
                          <th style={{ width: "10%" }}>Status</th>
                          <th style={{ width: "5%" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usersData.map((user) => (
                          <tr key={user.userId}>
                            <td style={{ width: "15%" }}>{user?.userName}</td>
                            <td style={{ width: "20%" }}>{user?.email}</td>
                            <td style={{ width: "15%" }}>
                              {user?.phoneNumber}
                            </td>
                            <td style={{ width: "10%" }}>
                              {(Array.isArray(user?.roleDetails) &&
                                user.roleDetails[0]?.role) ||
                                "N/A"}
                            </td>
                            <td style={{ width: "25%" }}>
                              {user?.description}
                            </td>
                            <td style={{ width: "10%" }}>
                              <FormGroup switch>
                                <Input
                                  type="switch"
                                  id={`user-status-${user.userId}`}
                                  checked={!!user.status}
                                  disabled={isLoading}
                                  onChange={() =>
                                    handleSwitchToggle(user.userId)
                                  }
                                  style={{
                                    height: "1.4em",
                                    width: "3em",
                                    cursor: "pointer",
                                  }}
                                />
                              </FormGroup>
                            </td>
                            <td style={{ width: "5%" }}>
                              <div className="d-flex gap-3 align-items-center mt-1">
                                <i
                                  onClick={() =>
                                    navigate(`/view-user/${user.userId}`)
                                  }
                                  className="fa-solid fa-eye action-icon"
                                  title="View"
                                ></i>
                                <i
                                  onClick={() =>
                                    navigate(`/add-user/${user.userId}`)
                                  }
                                  className="fa-regular fa-pen-to-square action-icon"
                                  title="Edit"
                                ></i>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </Row>
      </Container>
    </div>
  );
}

export default Users;
